import React, { useCallback, useLayoutEffect, useRef } from "react";

interface ColumnResizerProps {
  disabled?: boolean;
  minWidth?: number;
  className?: string;
}

// The props after default-parameter resolution — what propsRef.current holds.
type ResolvedColumnResizerProps = Required<ColumnResizerProps>;

/**
 * Drag-to-resize handle that sits between two columns and adjusts the widths of
 * its previous and next siblings while the user drags.
 *
 * This component is NOT an observer (the class was a plain React.Component), so
 * it stays a plain function component.
 *
 * Faithful-port notes (the behaviour is the spec):
 * - The class stored `dragging`, `mouseX`, `startPos`, `startWidthPrev` and
 *   `startWidthNext` as mutable instance fields (no setState, no re-render):
 *   they become refs here.
 * - The bound handlers (`startDrag`, `endDrag`, `onMouseMove`) read `this.props`
 *   live. We keep one stable identity per handler (via useCallback with no deps)
 *   and read the *current* props through a ref, so the function references added
 *   to `document` are the exact same references later removed — matching the
 *   original add/remove pairing across `disabled` toggles.
 * - Listeners are attached with NO capture flag (third arg omitted), exactly as
 *   the class did.
 * - cDM/cWU/cDU listener wiring is reproduced with a useLayoutEffect that runs
 *   on every render but only adds/removes on the `disabled` edge, mirroring the
 *   class lifecycle order (add on mount-if-enabled; remove on unmount-if-enabled;
 *   add/remove on the disabled -> enabled / enabled -> disabled transitions).
 */
function ColumnResizer({ disabled = false, minWidth = 0, className = "" }: ColumnResizerProps) {
  // minWidth is read live via propsRef inside onMouseMove (matching the class's
  // this.props.minWidth); only disabled and className are needed in render.
  //
  // Defaults are JavaScript default parameters (React 18.3 warns on
  // defaultProps for function components). They mirror the class-era
  // ColumnResizer.defaultProps = { disabled: false, minWidth: 0, className: "" }.

  const elementRef = useRef<HTMLDivElement>(null);

  // Mutable drag state (former instance fields — no re-render on change).
  const dragging = useRef(false);
  const mouseX = useRef(0);
  const startPos = useRef(0);
  const startWidthPrev = useRef(0);
  const startWidthNext = useRef(0);

  // Live props for the stable handlers (the class read this.props directly).
  // Rebuilt each render from the defaulted values so propsRef.current.minWidth
  // sees the default-applied value, exactly as this.props.minWidth did.
  const propsRef = useRef<ResolvedColumnResizerProps>({ disabled, minWidth, className });
  propsRef.current = { disabled, minWidth, className };

  const startDrag = useCallback(() => {
    if (propsRef.current.disabled) {
      return;
    }

    dragging.current = true;
    startPos.current = mouseX.current;

    startWidthPrev.current = 0;
    startWidthNext.current = 0;

    if (elementRef.current) {
      // The resizer sits between two column elements; its DOM siblings are
      // always HTMLElements (the original read `.clientWidth` / `.style`
      // directly). Cast at the boundary to the element surface we touch.
      const prevSibling = elementRef.current.previousSibling as HTMLElement | null;
      const nextSibling = elementRef.current.nextSibling as HTMLElement | null;

      if (prevSibling) {
        startWidthPrev.current = prevSibling.clientWidth;
      }

      if (nextSibling) {
        startWidthNext.current = nextSibling.clientWidth;
      }
    }
  }, []);

  const endDrag = useCallback(() => {
    if (propsRef.current.disabled) {
      return;
    }

    dragging.current = false;
  }, []);

  const onMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (propsRef.current.disabled) {
      return;
    }

    mouseX.current = "touches" in e ? e.touches[0].screenX : e.screenX;
    if (!dragging.current) {
      return;
    }

    const element = elementRef.current;

    const moveDiff = startPos.current - mouseX.current;
    let newPrev = startWidthPrev.current - moveDiff;
    let newNext = startWidthNext.current + moveDiff;

    if (newPrev < propsRef.current.minWidth) {
      const offset = newPrev - propsRef.current.minWidth;
      newPrev = propsRef.current.minWidth;
      newNext += offset;
    } else if (newNext < propsRef.current.minWidth) {
      const offset = newNext - propsRef.current.minWidth;
      newNext = propsRef.current.minWidth;
      newPrev += offset;
    }

    (element!.previousSibling as HTMLElement).style.width = newPrev + "px";
    (element!.nextSibling as HTMLElement).style.width = newNext + "px";
  }, []);

  const addEventListenersToDocument = useCallback(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", endDrag);

    document.addEventListener("touchmove", onMouseMove);
    document.addEventListener("touchend", endDrag);
  }, [onMouseMove, endDrag]);

  const removeEventListenersFromDocument = useCallback(() => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", endDrag);

    document.removeEventListener("touchmove", onMouseMove);
    document.removeEventListener("touchend", endDrag);
  }, [onMouseMove, endDrag]);

  // Reproduce the class lifecycle for listener wiring.
  //
  //   componentDidMount:    if (!disabled) add
  //   componentWillUnmount: if (!disabled) remove
  //   componentDidUpdate:   prev.disabled && !disabled -> add
  //                         !prev.disabled && disabled -> remove
  //
  // useLayoutEffect with a ref to the previous `disabled` value lets us tell
  // mount from update and only act on the same edges the class did. The cleanup
  // runs only on unmount (the effect has no deps), matching cWU.
  const prevDisabled = useRef<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    if (prevDisabled.current === undefined) {
      // mount (componentDidMount)
      if (!disabled) {
        addEventListenersToDocument();
      }
    } else {
      // update (componentDidUpdate)
      if (prevDisabled.current && !disabled) {
        addEventListenersToDocument();
      }

      if (!prevDisabled.current && disabled) {
        removeEventListenersFromDocument();
      }
    }

    prevDisabled.current = disabled;
  });

  useLayoutEffect(() => {
    // componentWillUnmount: only remove if currently enabled. We read the latest
    // `disabled` through propsRef so this matches the class guard at unmount
    // time.
    return () => {
      if (!propsRef.current.disabled) {
        removeEventListenersFromDocument();
      }
    };
  }, [removeEventListenersFromDocument]);

  const style: React.CSSProperties = {
    userSelect: "none"
  };

  if (!disabled) {
    style.cursor = "ew-resize";
  }

  // When disabled, the original passes `false` (no listener) to the DOM event
  // props. React treats `false` exactly like an absent handler; the cast keeps
  // the byte-identical `!disabled && startDrag` expression while satisfying the
  // handler prop type.
  const onMouseDownProp = (!disabled && startDrag) as React.MouseEventHandler<HTMLDivElement> | undefined;
  const onTouchStartProp = (!disabled && startDrag) as React.TouchEventHandler<HTMLDivElement> | undefined;

  return (
    <div
      ref={elementRef}
      style={style}
      className={className}
      onMouseDown={onMouseDownProp}
      onTouchStart={onTouchStartProp}
    />
  );
}

export default ColumnResizer;
